import { c as _c } from "react/compiler-runtime";
import { type Message as AgUiMessage } from '@ag-ui/client';
import { useTheme } from '@precis-digital/kurama/utils';
import { type Theme } from '@precis-digital/kurama/theme';
import { useEffect, useRef } from 'react';
import { type CopilotFrontendToolElementRef } from './useCopilotFrontendTool';
import { applyCopilotActionHighlight, applyCopilotFocusHighlight } from 'shared/copilot/utils/copilotElementHighlightApply';
import { getCompletedFrontendToolInvocations, getCopilotFrontendToolElementSelector, type CopilotElementHighlightEffect, type CopilotFrontendToolInvocation } from 'shared/copilot/utils/copilotElementHighlight';
import { scrollCopilotElementIntoView } from 'shared/copilot/utils/scrollCopilotElementIntoView';
const activeCopilotElementHighlightCleanups = new Set<() => void>();
const copilotFrontendToolElementRefsByToolName = new Map<string, CopilotFrontendToolElementRef[]>();
const completedCopilotFrontendToolCallIds = new Set<string>();

/**
 * Applies a highlight effect to all mounted element refs for a tool invocation.
 * Skips refs whose DOM nodes are not currently mounted.
 */
const applyHighlightToElementRefs = (elementRefs: CopilotFrontendToolElementRef[] | undefined, invocation: CopilotFrontendToolInvocation, applyHighlight: (element: HTMLElement, options: {
  highlightId: string;
  toolCallId: string;
  activeCleanups: Set<() => void>;
}, theme: Theme) => CopilotElementHighlightEffect, theme: Theme): CopilotElementHighlightEffect[] => elementRefs?.flatMap(elementRef => {
  const element = elementRef.current;
  if (element == null) return [];
  return [applyHighlight(element, {
    highlightId: invocation.toolCallId,
    toolCallId: invocation.toolCallId,
    activeCleanups: activeCopilotElementHighlightCleanups
  }, theme)];
}) ?? [];

/**
 * Registers DOM refs for a frontend tool so highlights can target affected page elements.
 * Returns an unregister callback invoked when the tool unmounts or its refs change.
 */
export const registerCopilotFrontendToolElementRefs = (toolName: string, elementRefs: CopilotFrontendToolElementRef[]): (() => void) => {
  copilotFrontendToolElementRefsByToolName.set(toolName, elementRefs);
  return () => {
    copilotFrontendToolElementRefsByToolName.delete(toolName);
  };
};
export { getCopilotFrontendToolElementSelector };

/**
 * Scrolls the DOM element tagged with a tool call id into the viewport.
 * Centers when it fits; tall elements scroll to their top.
 */
export const scrollCopilotFrontendToolElementIntoView = (toolCallId: string): boolean => {
  if (typeof document === 'undefined') return false;
  const element = document.querySelector<HTMLElement>(getCopilotFrontendToolElementSelector(toolCallId));
  if (element == null) return false;
  scrollCopilotElementIntoView(element);
  return true;
};

/**
 * Returns whether element refs have been registered for the given frontend tool name.
 * Used before attempting highlight or scroll effects for a tool.
 */
export const hasCopilotFrontendToolElementRefs = (toolName: string): boolean => copilotFrontendToolElementRefsByToolName.has(toolName);

/**
 * Applies a focus highlight and scrolls registered elements into view for a tool call.
 * Returns true when either highlight or scroll succeeded.
 */
export const highlightCopilotFrontendTool = (toolName: string, toolCallId: string, theme: Theme): boolean => {
  const elementRefs = copilotFrontendToolElementRefsByToolName.get(toolName);
  const didHighlight = applyHighlightToElementRefs(elementRefs, {
    toolCallId,
    toolName
  }, applyCopilotFocusHighlight, theme).length > 0;
  const didScroll = scrollCopilotFrontendToolElementIntoView(toolCallId);
  return didScroll || didHighlight;
};

/**
 * Clears all active copilot element highlight effects from the DOM.
 * Invokes every registered cleanup callback in the highlight registry.
 */
export const resetCopilotFrontendToolHighlights = (): void => {
  Array.from(activeCopilotElementHighlightCleanups).forEach(cleanup => {
    cleanup();
  });
};

/**
 * Resets highlight state, element ref registry, and completed tool-call tracking.
 * Called when leaving a page or resetting copilot session state.
 */
export const resetCopilotFrontendToolElementEffectState = (): void => {
  resetCopilotFrontendToolHighlights();
  copilotFrontendToolElementRefsByToolName.clear();
  completedCopilotFrontendToolCallIds.clear();
};

/**
 * Applies a one-shot focus highlight pulse to the given element refs.
 * Used by explicit highlight requests rather than automatic post-tool effects.
 */
export const pulseCopilotFrontendToolElementRefs = (elementRefs: CopilotFrontendToolElementRef[] | undefined, highlightId: string, theme: Theme): boolean => applyHighlightToElementRefs(elementRefs, {
  toolCallId: highlightId,
  toolName: 'highlight_element'
}, applyCopilotFocusHighlight, theme).length > 0;

/**
 * Schedules an automatic action highlight after React commits DOM updates from a tool handler.
 * Double `requestAnimationFrame` deferral avoids highlighting stale layout.
 */
const scheduleAutomaticToolHighlight = (invocation: CopilotFrontendToolInvocation, theme: Theme): void => {
  // Defer until after React commits DOM updates from the tool handler (e.g. setState).
  globalThis.requestAnimationFrame(() => {
    globalThis.requestAnimationFrame(() => {
      applyHighlightToElementRefs(copilotFrontendToolElementRefsByToolName.get(invocation.toolName), invocation, applyCopilotActionHighlight, theme);
    });
  });
};

/**
 * Watches AG-UI messages and auto-highlights page elements when frontend tools complete.
 *
 * Tracks completed tool calls to avoid re-highlighting on mount, then applies action
 * highlights to registered refs after handler-driven DOM updates settle.
 */
export const useCopilotFrontendToolElementEffects = messages => {
  const $ = _c(4);
  const theme = useTheme();
  const hasInitializedCompletedToolCallsRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== messages || $[1] !== theme) {
    t0 = () => {
      if (messages.length === 0) {
        return;
      }
      const completedInvocations = getCompletedFrontendToolInvocations(messages);
      if (!hasInitializedCompletedToolCallsRef.current) {
        completedInvocations.forEach(_temp);
        hasInitializedCompletedToolCallsRef.current = true;
        return;
      }
      completedInvocations.forEach(invocation_0 => {
        if (completedCopilotFrontendToolCallIds.has(invocation_0.toolCallId)) {
          return;
        }
        completedCopilotFrontendToolCallIds.add(invocation_0.toolCallId);
        scheduleAutomaticToolHighlight(invocation_0, theme);
      });
    };
    t1 = [messages, theme];
    $[0] = messages;
    $[1] = theme;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
};
function _temp(invocation) {
  return completedCopilotFrontendToolCallIds.add(invocation.toolCallId);
}